/**
 * Canonical section IDs shared across all entity create forms and bespoke
 * forms (Log Activity Communication/Task, etc).
 *
 * Using these IDs (rather than ad-hoc strings) means:
 *  - analytics can compare section open-rates across entities,
 *  - users see consistent labels for the same concept ("Scheduling",
 *    "Follow-up", "Custom fields") wherever they appear,
 *  - new entities are easy to slot in.
 *
 * Arbitrary IDs are still allowed; the taxonomy is a recommendation.
 */

import {
  Calendar,
  Bell,
  FileText,
  Database,
  MapPin,
  Mail,
  Users,
  Tags,
  Sliders,
  Layers,
  type LucideIcon,
} from "lucide-react";

export type FormSectionTaxonomyId =
  | "core"
  | "classification"
  | "relationships"
  | "location"
  | "contact-channels"
  | "scheduling"
  | "follow-up"
  | "description"
  | "custom"
  | "metadata";

export interface FormSectionTaxonomyEntry {
  id: FormSectionTaxonomyId;
  title: string;
  summary: string;
  icon: LucideIcon;
  /** Default kind hint — entities may override. */
  defaultKind: "always" | "collapsible";
}

export const FORM_SECTION_TAXONOMY: Record<FormSectionTaxonomyId, FormSectionTaxonomyEntry> = {
  core: {
    id: "core",
    title: "Core details",
    summary: "Required fields",
    icon: Layers,
    defaultKind: "always",
  },
  classification: {
    id: "classification",
    title: "Classification",
    summary: "Status, type, priority, tags",
    icon: Tags,
    defaultKind: "collapsible",
  },
  relationships: {
    id: "relationships",
    title: "Relationships",
    summary: "Company, contact, owner, assignee",
    icon: Users,
    defaultKind: "collapsible",
  },
  location: {
    id: "location",
    title: "Location",
    summary: "Address, city, state, zip, country",
    icon: MapPin,
    defaultKind: "collapsible",
  },
  "contact-channels": {
    id: "contact-channels",
    title: "Contact channels",
    summary: "Phone, email aliases, website, social",
    icon: Mail,
    defaultKind: "collapsible",
  },
  scheduling: {
    id: "scheduling",
    title: "Scheduling",
    summary: "Dates, times, reminders",
    icon: Calendar,
    defaultKind: "collapsible",
  },
  "follow-up": {
    id: "follow-up",
    title: "Follow-up",
    summary: "Schedule a reminder for next steps",
    icon: Bell,
    defaultKind: "collapsible",
  },
  description: {
    id: "description",
    title: "Description",
    summary: "Notes and long-form details",
    icon: FileText,
    defaultKind: "collapsible",
  },
  custom: {
    id: "custom",
    title: "Custom fields",
    summary: "Free-form metadata",
    icon: Sliders,
    defaultKind: "collapsible",
  },
  metadata: {
    id: "metadata",
    title: "Metadata",
    summary: "External IDs, source, raw metadata",
    icon: Database,
    defaultKind: "collapsible",
  },
};

/**
 * Helper — start a section from a taxonomy entry with sensible defaults.
 * Caller still supplies `render` and may override `kind`, `defaultOpen`,
 * `hasValue`, `title`, etc.
 *
 * Example:
 *   defineTaxonomySection("follow-up", {
 *     defaultOpen: form.watch("follow_up_schedule"),
 *     hasValue: (f) => Boolean(f.watch("follow_up_schedule")),
 *     render: (f) => <FollowUpSection control={f.control} ... />,
 *   });
 */
export function defineTaxonomySection<TFormValues extends import("react-hook-form").FieldValues = import("react-hook-form").FieldValues>(
  id: FormSectionTaxonomyId,
  overrides: {
    title?: string;
    summary?: string;
    kind?: "always" | "collapsible";
    defaultOpen?: boolean;
    hasValue?: (form: import("react-hook-form").UseFormReturn<TFormValues>) => boolean;
    render: (form: import("react-hook-form").UseFormReturn<TFormValues>) => import("react").ReactNode;
  }
): import("./types").FormSectionDescriptor<TFormValues> {
  const entry = FORM_SECTION_TAXONOMY[id];
  return {
    id,
    kind: overrides.kind ?? entry.defaultKind,
    title: overrides.title ?? entry.title,
    summary: overrides.summary ?? entry.summary,
    icon: entry.icon,
    defaultOpen: overrides.defaultOpen,
    hasValue: overrides.hasValue,
    render: overrides.render,
  };
}
